// src/hooks/useNotifications.tsx
// Orders/notifications, backed by the shared in-memory ordersStore (no IndexedDB).
// Pagination is per-instance (local `page`); the data array is shared across all
// hook instances, so the backend is hit once regardless of how many widgets mount.
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { Notification } from '../types/Notification';
import { MOCK_ORDERS_ENABLED } from '../mocks/config';
import { markOrderSold, markOrdersSold } from '../services/orderSync';
import { ordersStore } from '../stores/orderStores';

export function useNotifications(pageSize = 10) {
  const all = useSyncExternalStore(ordersStore.subscribe, ordersStore.getSnapshot);
  const loading = useSyncExternalStore(ordersStore.subscribe, ordersStore.getLoading);
  const [page, setPage] = useState(1);

  // Refetch on mount; concurrent mounts of multiple widgets dedupe to one request.
  useEffect(() => { void ordersStore.refresh(); }, []);

  const sorted = useMemo(
    () => [...all].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
    [all],
  );
  const total = sorted.length;
  const unread = useMemo(
    () => sorted.filter((n) => !n.isRead && n.status === 'new').length,
    [sorted],
  );
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), maxPage);
  const items = useMemo(() => {
    const offset = (safePage - 1) * pageSize;
    return sorted.slice(offset, offset + pageSize);
  }, [sorted, safePage, pageSize]);

  const markRead = useCallback(async (ids: string[]) => {
    ordersStore.markRead(ids ?? []);
  }, []);

  const remove = useCallback(async (ids: string[]) => {
    if (!ids?.length) return;
    if (!MOCK_ORDERS_ENABLED) {
      const phones = [...new Set(ids.map((id) => id.split('_')[0]).filter(Boolean))];
      await markOrdersSold(phones).catch((e) =>
        console.error('[useNotifications] markOrdersSold failed:', e),
      );
      // Order comes back as 'shipped' — refetch so the badge/modal drop it.
      await ordersStore.refresh();
    } else {
      ordersStore.removeLocal(ids);
    }
  }, []);

  // Mark a single order as sold. Real mode: backend + refetch; mock: drop locally.
  const markSold = useCallback(async (id: string, phone?: string) => {
    if (!MOCK_ORDERS_ENABLED && phone) {
      await markOrderSold(phone);
      await ordersStore.refresh();
    } else {
      ordersStore.removeLocal([id]);
    }
  }, []);

  const addNotification = useCallback(async (n: Notification) => {
    ordersStore.prepend(n);
    setPage(1);
  }, []);

  return {
    items, unread, total,
    page: safePage, setPage, pageSize, loading,
    reload: () => ordersStore.refresh(),
    markRead, remove, addNotification, markSold,
  };
}
